/**
 * PaymentHistoryDialog — unified payment history for one member.
 *
 * Two modes:
 *   • Member mode: fetches /payment-history/me
 *   • Admin mode: fetches /payment-history/{userId}
 *
 * Features:
 *   • Year filter dropdown (All / individual calendar years).
 *   • Status badges (paid / pending / refunded / cancelled / failed).
 *   • "Paid for partner (X)" / "Paid on your behalf by (X)" annotations.
 *   • CSV + PDF download buttons.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { Label } from "./components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Loader2, Download, FileText, Receipt, ArrowLeftRight, User as UserIcon } from "lucide-react";

const STATUS_STYLES = {
  paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  refunded: "bg-blue-100 text-blue-800 border-blue-200",
  cancelled: "bg-slate-100 text-slate-700 border-slate-200",
  failed: "bg-rose-100 text-rose-800 border-rose-200",
};

const TYPE_LABELS = {
  membership_fee: "Membership",
  event_booking: "Event",
  season_pass: "Season pass",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
};

const fmtMoney = (amount, currency) => {
  const n = Number(amount || 0);
  return `${(currency || "EUR") === "EUR" ? "€" : ""}${n.toFixed(2)}`;
};

const PaymentHistoryDialog = ({ isOpen, onClose, userId = "me", memberName = "" }) => {
  const [year, setYear] = useState("all");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const endpoint = userId === "me" ? "/payment-history/me" : `/payment-history/${userId}`;

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    const q = year === "all" ? "" : `?year=${year}`;
    axios
      .get(`${endpoint}${q}`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (!cancelled) toast.error(err.response?.data?.detail || "Failed to load payment history");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isOpen, endpoint, year]);

  // Build the year dropdown options from the rows themselves so we only offer
  // years the user actually has activity in (+ "All").
  const yearOptions = useMemo(() => {
    if (!data?.rows) return [];
    const years = new Set();
    for (const r of data.rows) {
      if (r.date) years.add(new Date(r.date).getFullYear());
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [data]);

  const downloadFile = async (kind) => {
    const q = year === "all" ? "" : `?year=${year}`;
    const url = `${endpoint}/export.${kind}${q}`;
    try {
      const res = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: kind === "csv" ? "text/csv" : "application/pdf",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const yr = year === "all" ? "all" : year;
      link.download = `payment_history_${yr}.${kind}`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to download ${kind.toUpperCase()}`);
    }
  };

  const title = memberName
    ? `Payment history — ${memberName}`
    : data?.user?.name
      ? `Payment history — ${data.user.name}`
      : "Payment history";

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid="payment-history-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-emerald-700" />
            {title}
          </DialogTitle>
          <DialogDescription>
            Every membership fee, event booking, and season pass involving this account.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-end justify-between gap-3 py-2 border-b border-slate-200">
          <div className="flex items-end gap-3">
            <div>
              <Label htmlFor="year-select" className="text-xs text-slate-600">Year</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(v === "all" ? "all" : parseInt(v, 10))}>
                <SelectTrigger id="year-select" className="h-9 w-32" data-testid="year-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All years</SelectItem>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => downloadFile("csv")}
              disabled={loading || !data?.rows?.length}
              data-testid="export-csv-btn"
            >
              <Download className="h-3 w-3 mr-1" /> CSV
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => downloadFile("pdf")}
              disabled={loading || !data?.rows?.length}
              data-testid="export-pdf-btn"
            >
              <FileText className="h-3 w-3 mr-1" /> PDF
            </Button>
          </div>
        </div>

        {/* Totals */}
        {data?.totals && !loading && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 py-3 text-xs" data-testid="totals-strip">
            <TotalCard label="Paid" amount={data.totals.paid} tone="emerald" />
            <TotalCard label="Pending" amount={data.totals.pending} tone="amber" />
            <TotalCard label="Refunded" amount={data.totals.refunded} tone="blue" />
            <TotalCard label="Other" amount={data.totals.other} tone="slate" />
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500" data-testid="history-loading">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : !data?.rows?.length ? (
          <div className="text-center py-14 text-slate-500" data-testid="history-empty">
            <Receipt className="h-8 w-8 mx-auto mb-2 opacity-40" />
            No payments recorded {year !== "all" ? `for ${year}` : "yet"}.
          </div>
        ) : (
          <Table data-testid="history-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-28">Date</TableHead>
                <TableHead className="w-28">Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-32">Note</TableHead>
                <TableHead className="w-24 text-right">Amount</TableHead>
                <TableHead className="w-24">Status</TableHead>
                <TableHead className="w-24">Method</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r, i) => (
                <TableRow key={`${r.reference_id}-${i}`} data-testid={`history-row-${i}`}>
                  <TableCell className="text-xs">{fmtDate(r.date)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] font-normal">
                      {TYPE_LABELS[r.type] || r.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{r.description}</TableCell>
                  <TableCell className="text-xs">
                    {r.paid_for && (
                      <span className="inline-flex items-center gap-1 text-indigo-700">
                        <ArrowLeftRight className="h-3 w-3" /> for {r.paid_for.name}
                      </span>
                    )}
                    {r.paid_by && (
                      <span className="inline-flex items-center gap-1 text-slate-600">
                        <UserIcon className="h-3 w-3" /> by {r.paid_by.name}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-sm">
                    {fmtMoney(r.amount, r.currency)}
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] font-medium border ${STATUS_STYLES[r.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                      {r.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs capitalize text-slate-600">{r.method}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
};

const TotalCard = ({ label, amount, tone }) => {
  const map = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  };
  return (
    <div className={`rounded-md border p-2 ${map[tone] || map.slate}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-sm font-bold">€{Number(amount || 0).toFixed(2)}</div>
    </div>
  );
};

export default PaymentHistoryDialog;
